import { useCallback, useMemo, useState } from 'react'
import { useArchiveVisuals, useDownloadArchive } from 'dataQueries/visual.query'

import { AnalyticsEvent } from 'utils/analytics'
import { ProductKind, ProductKindsWithDocumentGallery, ProductType } from 'constants/product'
import { VisualFileType } from 'constants/visual'
import { deriveProductTypeBucket } from 'utils/product'
import constate from 'constate'
import { mutationToAPIRequest, queryToAPIRequest } from 'utils/reactQuery/queryHelpers'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useGalleryAssignment } from './GalleryAssignment.context'
import { useGalleryDeal } from './GalleryDeal.context'
import { useGalleryVisualSelection } from './GalleryVisualSelection.context'
import { useGalleryVisualType } from './GalleryVisualType.context'
import { useUserData } from 'components/contexts/UserDataContext'

export const [GalleryVisualsDownloadArchiveContextProvider, useGalleryVisualsDownloadArchive] = constate(({
  assignmentId,
}: {
  assignmentId: string
}) => {
  const { roles } = useAuth0()
  const {
    nonPersonalUserWorkspaces
  } = useUserData()
  const {
    logGalleryEvent,
    product
  } = useGalleryAssignment()
  const { dealData } = useGalleryDeal()
  const {
    selectedPurchasedVisualsKeys,
    selectedNotPurchasedVisualsKeys,
  } = useGalleryVisualSelection()
  const {
    transformTypes,
  } = useGalleryVisualType()

  const [isDownloadModalOpen, setDownloadModalOpen] = useState(false)

  // Client archive flow
  const archiveClientMutation = useArchiveVisuals()
  const clientJobId = archiveClientMutation.data?.data?.id || null
  const downloadClientQuery = useDownloadArchive(assignmentId, clientJobId)

  // Admin archive flow
  const archiveAdminMutation = useArchiveVisuals()
  const adminJobId = archiveAdminMutation.data?.data?.id || null
  const downloadAdminQuery = useDownloadArchive(assignmentId, adminJobId, true)

  // Bridge to APIRequest shape for downstream consumers
  const initiateDownloadVisualsArchive = useMemo(() => archiveClientMutation.isIdle ? null : mutationToAPIRequest(archiveClientMutation), [archiveClientMutation])
  const downloadVisualsArchive = useMemo(() => archiveClientMutation.isIdle ? null : queryToAPIRequest(downloadClientQuery, {
    treatDataAsPending: (data) => data == null
  }), [archiveClientMutation.isIdle, downloadClientQuery])
  const archiveAdmin = useMemo(() => archiveAdminMutation.isIdle ? null : mutationToAPIRequest(archiveAdminMutation), [archiveAdminMutation])
  const downloadVisualsArchiveAdmin = useMemo(() => archiveAdminMutation.isIdle ? null : queryToAPIRequest(downloadAdminQuery, {
    treatDataAsPending: (data) => data == null
  }), [archiveAdminMutation.isIdle, downloadAdminQuery])

  const isArchiveDisabled = useMemo(() => {
    if (archiveClientMutation.isPending) return true
    if (!product) return true
    if (selectedPurchasedVisualsKeys.size === 0) return true
    if (selectedNotPurchasedVisualsKeys.size > 0) return true

    return false
  }, [archiveClientMutation.isPending, product, selectedNotPurchasedVisualsKeys.size, selectedPurchasedVisualsKeys.size])

  const typesToDownload = useMemo(() => {
    const types = transformTypes()
    // Bucket-derived instead of the deprecated wire `type` (ISSUE-2745).
    // The widening vs the legacy type read is deliberate and accepted (2026-07-31).
    // Matterport-with-visit is a floor plan by product ruling, and document deliverables keep their POST-only archive,
    // both decided by kind so the restriction survives whatever bucket the flags produce.
    const typeBucket = product && deriveProductTypeBucket(product)
    const isRestrictedByKind = !!product && (product.kind === ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT || ProductKindsWithDocumentGallery.has(product.kind))
    if (isRestrictedByKind || typeBucket === ProductType.VIDEO || typeBucket === ProductType.FLOOR_PLAN) {
      return [VisualFileType.POST]
    } else {
      return types
    }
  }, [transformTypes, product])

  const initiateArchiveDownload = useCallback(async (visualsKey?: Set<string>) => {
    if (isArchiveDisabled && !visualsKey) {
      logger.warn('Archive download aborted - archive is disabled', undefined, { context: { component: 'GalleryVisualsDownloadArchive', action: 'initiateArchiveDownload', metadata: { assignmentId, hasProduct: Boolean(product) } } })

      return
    }
    if (!product) {
      logger.warn('Archive download aborted - product not resolved', undefined, { context: { component: 'GalleryVisualsDownloadArchive', action: 'initiateArchiveDownload', metadata: { assignmentId } } })

      return
    }

    const downloadKeys = visualsKey || selectedPurchasedVisualsKeys

    // Trigger downloading of selected visuals
    archiveClientMutation.mutate({
      assignmentId,
      types: typesToDownload,
      filenames: Array.from(downloadKeys),
      isAdmin: false,
    })

    // Open the star rating popup and start the download cycle (toast notifications)
    setDownloadModalOpen(true)

    // Track archive download trigger
    logGalleryEvent(AnalyticsEvent.GALLERY_DOWNLOAD_TRIGGERED, {
      role: Array.from(roles.all.values()).join(','),
      workspaceIDs: nonPersonalUserWorkspaces.length ? nonPersonalUserWorkspaces.map(({ id }) => id).join(', ') : '',
      productCategory: product.category,
      productKind: product.kind,
      assignmentOwnerId: dealData?.customer.id,
      assignmentOwnerEmail: dealData?.customer.email,
    })

  }, [isArchiveDisabled, product, archiveClientMutation, assignmentId, typesToDownload, selectedPurchasedVisualsKeys, logGalleryEvent, roles.all, nonPersonalUserWorkspaces, dealData?.customer.id, dealData?.customer.email])

  const initiateAdminArchiveDownload = useCallback((types: string[], filenames: string[]) => {
    archiveAdminMutation.mutate({
      assignmentId,
      types,
      filenames,
      isAdmin: true,
    })
  }, [archiveAdminMutation, assignmentId])

  const resetClientArchive = useCallback(() => {
    archiveClientMutation.reset()
  }, [archiveClientMutation])

  return {
    assignmentId,
    isArchiveDisabled,
    isDownloadModalOpen,
    setDownloadModalOpen,
    initiateDownloadVisualsArchive,
    downloadVisualsArchive,
    archiveAdmin,
    downloadVisualsArchiveAdmin,
    initiateArchiveDownload,
    initiateAdminArchiveDownload,
    resetClientArchive,
    // Expose download URLs directly for convenience
    clientDownloadUrl: downloadClientQuery.data?.url,
    adminDownloadUrl: downloadAdminQuery.data?.url,
  }
})
